"use client";

import { useLang } from "@/contexts/language-context";
import { motion } from "framer-motion";
import { Tablet, Smartphone, Monitor } from "lucide-react";

type Card = {
  icon: React.ComponentType<{ className?: string }>;
  step: string;
  title: { en: string; es: string };
  body: { en: string; es: string };
  cost: string;
  costLabel: { en: string; es: string };
};

const CARDS: Card[] = [
  {
    icon: Tablet,
    step: "01",
    title: { en: "Your iPad", es: "Tu iPad" },
    body: {
      en: "You already own one. The POS runs on the iPad you have. We don't sell you a $1,200 register.",
      es: "Ya tienes uno. El POS corre en el iPad que tienes. No te vendemos una caja registradora de $1,200.",
    },
    cost: "$0",
    costLabel: { en: "for the device", es: "por el dispositivo" },
  },
  {
    icon: Smartphone,
    step: "02",
    title: { en: "One North terminal", es: "Un terminal North" },
    body: {
      en: "One compact PAX card terminal, set up with your Redy payments account. With dual pricing, card payers cover the processing cost, so it doesn't come out of your margin.",
      es: "Un terminal PAX compacto, configurado con tu cuenta de pagos de Redy. Con dual pricing, quien paga con tarjeta cubre el costo de procesamiento, así no sale de tu margen.",
    },
    cost: "$249",
    costLabel: { en: "one-time", es: "pago único" },
  },
  {
    icon: Monitor,
    step: "03",
    title: { en: "Optional kiosk", es: "Kiosko opcional" },
    body: {
      en: "Turn a spare iPad into a self-order kiosk. Customers tap their own order while you cook. No extra kiosk fee.",
      es: "Convierte un iPad extra en kiosko de auto-orden. El cliente arma su pedido mientras cocinas. Sin fee extra de kiosko.",
    },
    cost: "$0",
    costLabel: { en: "to add a kiosk", es: "por agregar kiosko" },
  },
];

export default function FoodTruckQsrCostStack() {
  const { t, lang } = useLang();

  return (
    <section className="py-24 px-6 bg-white">
      <div className="max-w-[1100px] mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.5 }}
          className="text-center mb-14"
        >
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-redy-orange mb-4">
            {t("The setup cost", "El costo de arranque")}
          </div>
          <h2 className="text-[40px] md:text-[60px] font-black leading-[1.02] tracking-tight text-redy-charcoal mb-4">
            {t("$0 software plan.", "Plan de software de $0.")}
            <br />
            <span className="redy-text-gradient">{t("Card fees, covered.", "Fees de tarjeta, cubiertos.")}</span>
          </h2>
          <p className="text-lg text-redy-secondary max-w-[560px] mx-auto font-medium">
            {t(
              "The Start plan is $0 a month when you process payments with Redy. The card terminal is a one-time $249. With dual pricing, card payers cover the processing cost.",
              "El plan Start cuesta $0 al mes cuando procesas tus pagos con Redy. El terminal es un pago único de $249. Con dual pricing, quien paga con tarjeta cubre el costo de procesamiento."
            )}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {CARDS.map((c, i) => {
            const Icon = c.icon;
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.5, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] }}
                className="relative bg-[#FAFAFA] border border-black/[0.04] rounded-3xl p-7 hover:shadow-[0_20px_40px_-20px_rgba(0,0,0,0.12)] transition-shadow"
              >
                <div className="font-mono tabular-nums text-[10px] font-bold text-redy-charcoal/40 mb-5">
                  {c.step}
                </div>
                <div className="w-12 h-12 bg-redy-charcoal rounded-xl flex items-center justify-center mb-5">
                  <Icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-black text-redy-charcoal mb-2">
                  {c.title[lang as "en" | "es"] ?? c.title.en}
                </h3>
                <p className="text-sm text-redy-secondary leading-relaxed mb-6">
                  {c.body[lang as "en" | "es"] ?? c.body.en}
                </p>
                <div className="pt-5 border-t border-black/5">
                  <div className="font-mono tabular-nums text-3xl font-black redy-text-gradient leading-none">
                    {c.cost}
                  </div>
                  <div className="text-[11px] font-bold uppercase tracking-wider text-redy-charcoal/50 mt-1">
                    {c.costLabel[lang as "en" | "es"] ?? c.costLabel.en}
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Compare bar */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-3"
        >
          <div className="bg-redy-warm-white border border-black/[0.04] rounded-2xl p-5">
            <div className="text-[10px] font-bold uppercase tracking-wider text-redy-charcoal/50 mb-1">
              {t("Software", "Software")}
            </div>
            <div className="font-bold text-redy-charcoal text-lg">
              $0/{t("mo", "mes")}{" "}
              <span className="text-sm text-redy-charcoal/60 font-medium">
                {t("on Start, processing with Redy", "en Start, procesando con Redy")}
              </span>
            </div>
          </div>
          <div className="bg-redy-warm-white border border-black/[0.04] rounded-2xl p-5">
            <div className="text-[10px] font-bold uppercase tracking-wider text-redy-charcoal/50 mb-1">
              {t("Terminal", "Terminal")}
            </div>
            <div className="font-bold text-redy-charcoal text-lg">
              $249{" "}
              <span className="text-sm text-redy-charcoal/60 font-medium">
                {t("one-time, no lease", "pago único, sin renta")}
              </span>
            </div>
          </div>
          <div className="bg-redy-charcoal text-white rounded-2xl p-5 ring-2 ring-redy-orange">
            <div className="text-[10px] font-bold uppercase tracking-wider text-redy-orange mb-1">
              {t("Card processing", "Procesamiento")}
            </div>
            <div className="font-bold text-lg">
              {t("Covered", "Cubierto")}{" "}
              <span className="text-sm text-white/60 font-medium">
                {t("by card payers, with dual pricing", "por quien paga con tarjeta, con dual pricing")}
              </span>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
